/**
 * QSL Service
 * 
 * Service for handling QSL record-related API calls.
 */

import { apiConfig } from "@/config/api";
import { apiFetch } from "@/lib/apiFetch";

export interface QSLRecord {
  _id: string;
  user: {
    _id: string;
    callsign: string;
    nama_lengkap: string;
  };
  targetCallsign: string;
  qslDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface QSLRecordListResponse {
  data: QSLRecord[];
  total: number;
}

export interface CreateQSLRecordPayload {
  userId: string;
  targetCallsign: string;
  qslDate?: string;
}

export interface QSLRecordFilters {
  userId?: string;
  callsign?: string;
  dateFrom?: string;
  dateTo?: string;
}

class QSLService {
  /**
   * Create a QSL record
   * @param payload - QSL record data
   * @returns Promise with created records
   */
  async createQSLRecord(payload: CreateQSLRecordPayload): Promise<{ success: boolean; data: QSLRecord[]; message: string }> {
    try {
      const response = await apiFetch(`${apiConfig.baseURL}/api/qsl`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error creating QSL record:", error);
      throw error;
    }
  }

  /**
   * Get QSL records for a specific user
   * @param userId - User ID
   * @param filters - Optional filters
   * @returns Promise with QSL records
   */
  async getUserQSLRecords(userId: string, filters?: QSLRecordFilters): Promise<QSLRecordListResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.callsign) {
        params.append("callsign", filters.callsign);
      }
      if (filters?.dateFrom) {
        params.append("dateFrom", filters.dateFrom);
      }
      if (filters?.dateTo) {
        params.append("dateTo", filters.dateTo);
      }

      const url = `${apiConfig.baseURL}/api/qsl/user/${userId}${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response = await apiFetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data: QSLRecordListResponse = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching user QSL records:", error);
      throw error;
    }
  }

  /**
   * Search/filter QSL records
   * @param filters - Filter options
   * @returns Promise with filtered QSL records
   */
  async searchQSLRecords(filters?: QSLRecordFilters): Promise<QSLRecordListResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.userId) {
        params.append("userId", filters.userId);
      }
      if (filters?.callsign) {
        params.append("callsign", filters.callsign);
      }
      if (filters?.dateFrom) {
        params.append("dateFrom", filters.dateFrom);
      }
      if (filters?.dateTo) {
        params.append("dateTo", filters.dateTo);
      }

      const url = `${apiConfig.baseURL}/api/qsl/search${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response = await apiFetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data: QSLRecordListResponse = await response.json();
      return data;
    } catch (error) {
      console.error("Error searching QSL records:", error);
      throw error;
    }
  }
}

export const qslService = new QSLService();

